import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Sparkles, ShoppingCart, Loader2, ImageIcon, Lock, Palette, Layout, Plus, FolderOpen, Upload, Camera, Ruler, X, Download } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import Navbar from "../components/Navbar";

const AIVisualizerPage = () => {
  const navigate = useNavigate();
  const { user, token, refreshUser } = useAuth();
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [roomDescription, setRoomDescription] = useState("");
  const [style, setStyle] = useState("modern");
  const [generating, setGenerating] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [shoppingList, setShoppingList] = useState([]);
  const [credits, setCredits] = useState(null);
  // Project & Style Consistency
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [roomName, setRoomName] = useState("");
  const [showStylePanel, setShowStylePanel] = useState(false);
  const [styleProfile, setStyleProfile] = useState({
    primary_colour: "", secondary_colour: "", accent_colour: "",
    materials: [], style: "modern"
  });
  // Floor Plan
  const [floorPlanDescription, setFloorPlanDescription] = useState("");
  const [showFloorPlan, setShowFloorPlan] = useState(false);
  // Uploads & Dimensions
  const [floorPlanImage, setFloorPlanImage] = useState(null);
  const [floorPlanPreview, setFloorPlanPreview] = useState(null);
  const [roomPhotos, setRoomPhotos] = useState([]);
  const [roomPhotosPreviews, setRoomPhotosPreviews] = useState([]);
  const [ceilingHeight, setCeilingHeight] = useState("");
  const [kneeWallHeight, setKneeWallHeight] = useState("");

  const styles = [
    { value: "modern", label: "Modern Minimalist" },
    { value: "scandinavian", label: "Scandinavian" },
    { value: "industrial", label: "Industrial" },
    { value: "bohemian", label: "Bohemian" },
    { value: "traditional", label: "Traditional" },
    { value: "contemporary", label: "Contemporary" },
    { value: "rustic", label: "Rustic Farmhouse" },
    { value: "mid-century", label: "Mid-Century Modern" }
  ];

  const materialOptions = ["Wood", "Marble", "Concrete", "Brass", "Glass", "Linen", "Velvet", "Rattan", "Ceramic", "Terrazzo"];

  useEffect(() => {
    fetchProducts();
    fetchCredits();
    fetchProjects();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products?limit=20`);
      setProducts(response.data);
    } catch (error) { console.error("Failed to fetch products"); }
  };

  const fetchCredits = async () => {
    try {
      const response = await axios.get(`${API}/my-credits`, { headers: { Authorization: `Bearer ${token}` } });
      setCredits(response.data);
    } catch (error) { console.error("Failed to fetch credits"); }
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`, { headers: { Authorization: `Bearer ${token}` } });
      setProjects(response.data.projects);
    } catch (error) { console.error("Failed to fetch projects"); }
  };

  const createProject = async () => {
    const name = prompt("Enter property name:");
    if (!name) return;
    try {
      const response = await axios.post(`${API}/projects`, { name, style_profile: styleProfile }, { headers: { Authorization: `Bearer ${token}` } });
      fetchProjects();
      setSelectedProject(response.data.id);
      toast.success("Project created");
    } catch (error) { toast.error("Failed to create project"); }
  };

  const toggleProduct = (productId) => {
    setSelectedProducts(prev => prev.includes(productId) ? prev.filter(id => id !== productId) : [...prev, productId]);
  };

  const toggleMaterial = (mat) => {
    setStyleProfile(prev => ({
      ...prev,
      materials: prev.materials.includes(mat) ? prev.materials.filter(m => m !== mat) : [...prev.materials, mat]
    }));
  };

  const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const handleFloorPlanUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFloorPlanPreview(URL.createObjectURL(file));
    const b64 = await fileToBase64(file);
    setFloorPlanImage(b64);
    toast.success("Floor plan uploaded");
  };

  const handleRoomPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    const previews = files.map(f => URL.createObjectURL(f));
    const b64s = await Promise.all(files.map(fileToBase64));
    setRoomPhotosPreviews(prev => [...prev, ...previews]);
    setRoomPhotos(prev => [...prev, ...b64s]);
    toast.success(`${files.length} photo(s) added`);
  };

  const removeRoomPhoto = (index) => {
    setRoomPhotosPreviews(prev => prev.filter((_, i) => i !== index));
    setRoomPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleGenerate = async () => {
    if (!roomDescription.trim()) { toast.error("Please describe your room"); return; }
    setGenerating(true);
    setGeneratedImage(null);
    try {
      const payload = {
        room_description: roomDescription,
        style,
        product_ids: selectedProducts,
        project_id: selectedProject && selectedProject !== "none" ? selectedProject : undefined,
        room_name: roomName || undefined,
        floor_plan_description: floorPlanDescription || undefined,
        style_profile: showStylePanel ? styleProfile : undefined,
        floor_plan_image: floorPlanImage || undefined,
        room_photos: roomPhotos.length > 0 ? roomPhotos : undefined,
        ceiling_height: ceilingHeight ? parseFloat(ceilingHeight) : undefined,
        knee_wall_height: kneeWallHeight ? parseFloat(kneeWallHeight) : undefined
      };
      const response = await axios.post(`${API}/ai/visualize`, payload, { headers: { Authorization: `Bearer ${token}` } });
      setGeneratedImage(response.data.image_base64);
      setShoppingList(response.data.shopping_list);
      fetchCredits();
      await refreshUser();
      toast.success("Room visualisation generated!");
    } catch (error) {
      if (error.response?.status === 402) {
        toast.error("No credits remaining. Purchase a credit pack or subscribe.");
      } else {
        toast.error(error.response?.data?.detail || "Failed to generate image");
      }
    } finally { setGenerating(false); }
  };

  const addAllToCart = async () => {
    if (selectedProject) {
      try {
        const response = await axios.post(`${API}/projects/${selectedProject}/purchase-all`, {}, { headers: { Authorization: `Bearer ${token}` } });
        toast.success(response.data.message);
        navigate("/cart");
      } catch (error) { toast.error("Failed to add items"); }
    } else {
      try {
        for (const item of shoppingList) {
          await axios.post(`${API}/cart/add`, { product_id: item.id, quantity: 1 }, { headers: { Authorization: `Bearer ${token}` } });
        }
        toast.success("All items added to cart!");
        navigate("/cart");
      } catch (error) { toast.error("Failed to add items to cart"); }
    }
  };

  const canGenerate = credits?.has_subscription || (credits?.total_available === "unlimited") || (typeof credits?.total_available === "number" && credits.total_available > 0);

  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-light text-[#1A1B1A]">
              Spatial Design Engine
            </h1>
            <p className="text-[#5C5D5C] mt-2">Describe your room, upload a floor plan, and visualise products in a realistic design</p>
          </div>
          <div className="text-right" data-testid="credits-display">
            <p className="text-sm text-[#5C5D5C]">Credits remaining</p>
            <p className="text-2xl font-light text-[#20C997]">{credits?.total_available ?? "..."}</p>
            {credits && !credits.has_subscription && credits.total_available !== "unlimited" && (
              <Button onClick={() => navigate("/subscription")} variant="ghost" className="text-xs text-[#F06570] mt-1 p-0 h-auto">
                Get more credits
              </Button>
            )}
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left: Input Section */}
          <div className="space-y-6">
            {/* Project Selector */}
            <div className="dashboard-card" data-testid="project-section">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium text-[#1A1B1A] flex items-center gap-2"><FolderOpen size={18} /> Property Project</h3>
                <Button onClick={createProject} variant="ghost" size="sm" className="text-[#20C997] text-xs"><Plus size={14} className="mr-1" /> New Project</Button>
              </div>
              <Select value={selectedProject} onValueChange={setSelectedProject}>
                <SelectTrigger className="rounded-none border-[#EAE6DF]" data-testid="project-select">
                  <SelectValue placeholder="None (standalone design)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None (standalone design)</SelectItem>
                  {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name} ({p.rooms?.length || 0} rooms)</SelectItem>)}
                </SelectContent>
              </Select>
              {selectedProject && selectedProject !== "none" && (
                <Input value={roomName} onChange={(e) => setRoomName(e.target.value)} placeholder="Room name (e.g. Master Bedroom)" className="mt-3 rounded-none border-[#EAE6DF]" data-testid="room-name-input" />
              )}
            </div>

            {/* Room Description */}
            <div className="dashboard-card">
              <h3 className="font-medium text-[#1A1B1A] mb-3">Describe Your Room</h3>
              <Textarea value={roomDescription} onChange={(e) => setRoomDescription(e.target.value)} placeholder="E.g., A spacious living room with large bay windows, oak hardwood floors, and 3m ceilings..." className="min-h-[100px] rounded-none border-[#EAE6DF]" data-testid="room-description" />
            </div>

            {/* Uploads: Floor Plan & Room Photos */}
            <div className="dashboard-card">
              <h3 className="font-medium text-[#1A1B1A] mb-4 flex items-center gap-2"><Upload size={18} /> Upload References</h3>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm text-[#5C5D5C] mb-2">2D Floor Plan</label>
                  <label className="flex flex-col items-center justify-center border-2 border-dashed border-[#EAE6DF] hover:border-[#20C997] p-4 cursor-pointer transition-colors" data-testid="upload-floor-plan">
                    {floorPlanPreview ? (
                      <img src={floorPlanPreview} alt="Floor plan" className="w-full h-24 object-contain" />
                    ) : (
                      <>
                        <Layout size={28} className="text-[#8E8F8E] mb-2" />
                        <span className="text-xs text-[#8E8F8E]">Upload Floor Plan</span>
                      </>
                    )}
                    <input type="file" accept="image/*" onChange={handleFloorPlanUpload} className="hidden" />
                  </label>
                </div>
                <div>
                  <label className="block text-sm text-[#5C5D5C] mb-2">Room Photos</label>
                  <label className="flex flex-col items-center justify-center border-2 border-dashed border-[#EAE6DF] hover:border-[#20C997] p-4 cursor-pointer transition-colors" data-testid="upload-room-photos">
                    <Camera size={28} className="text-[#8E8F8E] mb-2" />
                    <span className="text-xs text-[#8E8F8E]">Upload Photos</span>
                    <input type="file" accept="image/*" multiple onChange={handleRoomPhotos} className="hidden" />
                  </label>
                </div>
              </div>
              {roomPhotosPreviews.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {roomPhotosPreviews.map((src, i) => (
                    <div key={i} className="relative w-16 h-16">
                      <img src={src} alt="" className="w-full h-full object-cover border border-[#EAE6DF]" />
                      <button type="button" onClick={() => removeRoomPhoto(i)} className="absolute -top-1 -right-1 w-5 h-5 bg-[#F06570] text-white rounded-full flex items-center justify-center"><X size={10} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Room Dimensions */}
            <div className="dashboard-card">
              <h3 className="font-medium text-[#1A1B1A] mb-4 flex items-center gap-2"><Ruler size={18} /> Room Dimensions</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-[#5C5D5C] mb-1 block">Ceiling Height (metres)</label>
                  <Input type="number" step="0.1" min="0" value={ceilingHeight} onChange={(e) => setCeilingHeight(e.target.value)} placeholder="e.g. 2.7" className="rounded-none border-[#EAE6DF]" data-testid="ceiling-height" />
                </div>
                <div>
                  <label className="text-xs text-[#5C5D5C] mb-1 block">Knee Wall / Loft Height (metres)</label>
                  <Input type="number" step="0.1" min="0" value={kneeWallHeight} onChange={(e) => setKneeWallHeight(e.target.value)} placeholder="e.g. 1.2" className="rounded-none border-[#EAE6DF]" data-testid="knee-wall-height" />
                </div>
              </div>
            </div>

            {/* 2D Floor Plan Description */}
            <div className="dashboard-card">
              <button onClick={() => setShowFloorPlan(!showFloorPlan)} className="w-full flex items-center justify-between" data-testid="floor-plan-toggle">
                <h3 className="font-medium text-[#1A1B1A] flex items-center gap-2"><Layout size={18} /> Floor Plan Description</h3>
                <span className="text-xs text-[#20C997]">{showFloorPlan ? "Hide" : "Add"}</span>
              </button>
              {showFloorPlan && (
                <Textarea value={floorPlanDescription} onChange={(e) => setFloorPlanDescription(e.target.value)} placeholder="Describe the room dimensions and layout: e.g., 5m x 4m rectangular room. Door on the south wall, two windows on the east wall..." className="mt-3 min-h-[80px] rounded-none border-[#EAE6DF]" data-testid="floor-plan-input" />
              )}
            </div>

            {/* Style Consistency */}
            <div className="dashboard-card">
              <button onClick={() => setShowStylePanel(!showStylePanel)} className="w-full flex items-center justify-between" data-testid="style-panel-toggle">
                <h3 className="font-medium text-[#1A1B1A] flex items-center gap-2"><Palette size={18} /> Style Consistency</h3>
                <span className="text-xs text-[#20C997]">{showStylePanel ? "Hide" : "Customise"}</span>
              </button>
              {showStylePanel && (
                <div className="mt-4 space-y-3">
                  <p className="text-xs text-[#5C5D5C]">Set colours and materials to maintain consistency across rooms</p>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs text-[#5C5D5C] mb-1 block">Primary Colour</label>
                      <div className="flex gap-2 items-center">
                        <input type="color" value={styleProfile.primary_colour || "#FFFFFF"} onChange={(e) => setStyleProfile({...styleProfile, primary_colour: e.target.value})} className="w-8 h-8 border-0 p-0 cursor-pointer" />
                        <Input value={styleProfile.primary_colour} onChange={(e) => setStyleProfile({...styleProfile, primary_colour: e.target.value})} placeholder="#hex" className="rounded-none border-[#EAE6DF] text-xs h-8" />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-[#5C5D5C] mb-1 block">Secondary Colour</label>
                      <div className="flex gap-2 items-center">
                        <input type="color" value={styleProfile.secondary_colour || "#FFFFFF"} onChange={(e) => setStyleProfile({...styleProfile, secondary_colour: e.target.value})} className="w-8 h-8 border-0 p-0 cursor-pointer" />
                        <Input value={styleProfile.secondary_colour} onChange={(e) => setStyleProfile({...styleProfile, secondary_colour: e.target.value})} placeholder="#hex" className="rounded-none border-[#EAE6DF] text-xs h-8" />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-[#5C5D5C] mb-1 block">Accent Colour</label>
                      <div className="flex gap-2 items-center">
                        <input type="color" value={styleProfile.accent_colour || "#FFFFFF"} onChange={(e) => setStyleProfile({...styleProfile, accent_colour: e.target.value})} className="w-8 h-8 border-0 p-0 cursor-pointer" />
                        <Input value={styleProfile.accent_colour} onChange={(e) => setStyleProfile({...styleProfile, accent_colour: e.target.value})} placeholder="#hex" className="rounded-none border-[#EAE6DF] text-xs h-8" />
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-[#5C5D5C] mb-2 block">Materials</label>
                    <div className="flex flex-wrap gap-2">
                      {materialOptions.map(mat => (
                        <button key={mat} type="button" onClick={() => toggleMaterial(mat)} className={`text-xs px-3 py-1.5 border transition-colors ${styleProfile.materials.includes(mat) ? "border-[#20C997] bg-[#20C997]/10 text-[#20C997]" : "border-[#EAE6DF] text-[#5C5D5C] hover:border-[#20C997]/50"}`}>
                          {mat}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Style Selector */}
            <div className="dashboard-card">
              <h3 className="font-medium text-[#1A1B1A] mb-3">Design Style</h3>
              <Select value={style} onValueChange={setStyle}>
                <SelectTrigger className="rounded-none border-[#EAE6DF]" data-testid="style-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {styles.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Product Selection */}
            <div className="dashboard-card">
              <h3 className="font-medium text-[#1A1B1A] mb-3">Select Products (Optional)</h3>
              <p className="text-sm text-[#5C5D5C] mb-3">Choose products to include in your visualisation</p>
              <div className="grid grid-cols-2 gap-3 max-h-60 overflow-y-auto" data-testid="product-selection">
                {products.map((product) => (
                  <label key={product.id} className={`flex items-center gap-3 p-3 border cursor-pointer transition-all ${selectedProducts.includes(product.id) ? "border-[#20C997] bg-[#20C997]/5" : "border-[#EAE6DF] hover:border-[#20C997]/50"}`}>
                    <Checkbox checked={selectedProducts.includes(product.id)} onCheckedChange={() => toggleProduct(product.id)} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#1A1B1A] truncate">{product.name}</p>
                      <p className="text-xs text-[#5C5D5C]">£{product.price}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Generate Button */}
            {!canGenerate ? (
              <div className="dashboard-card bg-[#EFECE5]">
                <div className="flex items-center gap-3">
                  <Lock className="text-[#F06570]" size={24} />
                  <div>
                    <p className="font-medium text-[#1A1B1A]">No credits remaining</p>
                    <p className="text-sm text-[#5C5D5C]">Purchase a credit pack or subscribe for unlimited visualisations</p>
                  </div>
                </div>
                <Button onClick={() => navigate("/subscription")} className="w-full mt-4 bg-[#F06570] hover:bg-[#D9525D] text-white rounded-none" data-testid="upgrade-btn">
                  View Plans & Credit Packs
                </Button>
              </div>
            ) : (
              <Button onClick={handleGenerate} disabled={generating || !roomDescription.trim()} className="w-full bg-[#20C997] hover:bg-[#1AAE82] text-white rounded-none py-6 text-base font-medium" data-testid="generate-btn">
                {generating ? (<><Loader2 size={20} className="mr-2 animate-spin" /> Generating... (this may take a minute)</>) : (<><Sparkles size={20} className="mr-2" /> Generate Visualisation</>)}
              </Button>
            )}
          </div>

          {/* Right: Output Section */}
          <div className="space-y-6">
            <div className="dashboard-card min-h-[400px] flex flex-col items-center justify-center" data-testid="generated-image-container">
              {generating ? (
                <div className="text-center">
                  <Loader2 size={48} className="mx-auto text-[#20C997] animate-spin mb-4" />
                  <p className="text-[#5C5D5C]">Creating your design...</p>
                  <p className="text-sm text-[#8E8F8E] mt-2">This may take up to a minute</p>
                </div>
              ) : generatedImage ? (
                <div className="w-full relative">
                  <img src={`data:image/png;base64,${generatedImage}`} alt="Generated room visualisation" className="w-full h-auto max-h-[500px] object-contain" data-testid="generated-image" />
                  <Button
                    onClick={() => {
                      const link = document.createElement("a");
                      link.href = `data:image/png;base64,${generatedImage}`;
                      link.download = `arky-world-design-${Date.now()}.png`;
                      link.click();
                    }}
                    className="mt-4 w-full bg-[#1A1B1A] hover:bg-[#333] text-white rounded-none py-3"
                    data-testid="download-design-btn"
                  >
                    <Download size={18} className="mr-2" /> Download Design
                  </Button>
                </div>
              ) : (
                <div className="text-center text-[#8E8F8E]">
                  <ImageIcon size={64} className="mx-auto mb-4 opacity-50" />
                  <p>Your AI-generated room will appear here</p>
                </div>
              )}
            </div>

            {/* Shopping List with Purchase Entire Look */}
            {shoppingList.length > 0 && (
              <div className="dashboard-card" data-testid="shopping-list">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-medium text-[#1A1B1A]">Shopping List</h3>
                </div>
                <div className="space-y-3">
                  {shoppingList.map((item) => (
                    <div key={item.id} className="flex items-center gap-3 p-3 bg-[#F7F5F0]">
                      {item.image && <img src={item.image} alt={item.name} className="w-12 h-12 object-cover" />}
                      <div className="flex-1">
                        <p className="font-medium text-[#1A1B1A]">{item.name}</p>
                        <p className="text-sm text-[#5C5D5C]">£{item.price}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t border-[#EAE6DF] flex justify-between mb-4">
                  <span className="font-medium text-[#1A1B1A]">Total</span>
                  <span className="font-medium text-[#1A1B1A]">£{shoppingList.reduce((sum, item) => sum + item.price, 0).toFixed(2)}</span>
                </div>
                <Button onClick={addAllToCart} className="w-full bg-[#20C997] hover:bg-[#1AAE82] text-white rounded-none py-4 text-base font-medium" data-testid="purchase-entire-look">
                  <ShoppingCart size={18} className="mr-2" /> Purchase Entire Look
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIVisualizerPage;
